import { CustomerIcon } from '@plato/ui-legacy/assets/icons/customer-icon';
import { DialogForm } from '@plato/ui-legacy/components/dialog/dialog-form';
import { DialogStateManager, useDialog } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { LoadingBlockFullPage } from '@plato/ui-legacy/components/loading/loading-block-full-page';
import { Button } from '@plato/ui-legacy/components/ui/button';
import SmallSpinner from '@plato/ui-legacy/components/ui/small-spinner';
import { Text } from '@plato/ui-legacy/components/ui/text';
import { Switch } from '@plato/ui-legacy/components/ui/toggle';
import { useToast } from '@plato/ui-legacy/components/ui/use-toast';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import { useDiscardChangesWarning } from '@plato/ui-legacy/lib/hooks/use-discard-changes-warning';
import { appendPlainTextToLexical } from '@plato/ui-legacy/lib/rich-text/append-plain-text-to-lexical';
import { Dialog } from '@plato/ui/containers/dialog';
import { intlFormat } from 'date-fns';
import { CalendarIcon, ClockIcon, Trash2Icon } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useVoiceRecording } from '../../../lib/contexts/voice-recording-context.tsx';
import type { CrmNoteInput } from '../../../server/dto/crm-note-input-dto.ts';
import type { RouterOutput } from '../../../server/types/infer-trpc-types.ts';
import { api } from '../../../utils/api.ts';
import type { ModalInnerProps } from '../../modal/index.tsx';
import { usePinNote } from '../hooks/use-pin-note.ts';
import { useUnpinNote } from '../hooks/use-unpin-note.ts';
import { useUpdateNote } from '../hooks/use-update-note.ts';
import { NoteDeleteDialog } from './note-delete-dialog.tsx';
import { NoteFormSection } from './note-form-section.tsx';

type NoteDetails = RouterOutput['platoUser']['query']['notes']['getNoteById'];

export type EditNoteFormProps = {
    note: NoteDetails;
    onCancel?: () => void;
    onSuccess?: () => void;
    onDeleted?: () => void;
    onIsDirtyChange?: (isDirty: boolean) => void;
};

export function EditNoteForm({
    note,
    onSuccess,
    onCancel,
    onDeleted,
    onIsDirtyChange,
}: EditNoteFormProps): ReactNode {
    const { t } = useTranslation();
    const locale = useResolvedLocale();
    const { toast } = useToast();
    const { currentSessionId } = useVoiceRecording();

    const handleDeleteClick = (): void => {
        void DialogStateManager.show(NoteDeleteDialog, {
            noteId: note.noteId,
            onDeleted,
        });
    };

    const { pin, isPending: isPinning } = usePinNote(note);
    const { unpin, isPending: isUnpinning } = useUnpinNote(note);
    const isPinPending = isPinning || isUnpinning;
    const isPinned = !!note.pinnedAt;

    const handlePinToggle = (checked: boolean): void => {
        if (checked) {
            pin();
        } else {
            unpin();
        }
    };

    const {
        control,
        handleSubmit,
        getValues,
        setValue,
        formState: { errors: formErrors, isDirty },
    } = useForm<CrmNoteInput>({
        defaultValues: {
            title: note.titleRich,
            attachments: note.attachments,
            interactionHappened: note.interactionHappened,
        },
    });

    useEffect(() => {
        onIsDirtyChange?.(isDirty);
    }, [isDirty, onIsDirtyChange]);

    const updateMutation = useUpdateNote({
        onSuccess: () => {
            toast({
                icon: 'success',
                variant: 'default',
                title: t('notes__form_edit_toast__success_title', { ns: 'notes' }),
            });
            onSuccess?.();
        },
        onError: (message) => {
            toast({
                icon: 'warning',
                title: 'Error',
                description: message,
            });
        },
    });

    const onSubmit = handleSubmit((data) => {
        updateMutation.mutate({
            ...data,
            noteId: note.noteId,
        });
    });

    return (
        <DialogForm>
            <DialogForm.Header title={t('notes__form_edit_title', { ns: 'notes' })} />
            <DialogForm.Content className="[@media(min-height:700px)]:min-h-[500px]">
                {note.noteType === 'NOTE' && (
                    <div className="flex items-center gap-2">
                        {isPinPending ? (
                            <div className="flex h-4 w-4 items-center justify-center">
                                <SmallSpinner />
                            </div>
                        ) : (
                            <Switch checked={isPinned} onCheckedChange={handlePinToggle} />
                        )}
                        <Text className="text-sm text-neutral-700">
                            {t('notes__pin_note', { ns: 'notes' })}
                        </Text>
                    </div>
                )}
                <DialogForm.ItemList grid>
                    {/* Customer Field (read-only) */}
                    <DialogForm.Item className="items-start">
                        <DialogForm.Label icon={CustomerIcon}>
                            {t('notes__form__customer_label', { ns: 'notes' })}
                        </DialogForm.Label>
                        <DialogForm.Body className="flex flex-col gap-1 py-1.5 px-2">
                            <Text color="neutral-950">{note.customer.customerName}</Text>
                        </DialogForm.Body>
                    </DialogForm.Item>

                    {/* Date and Time (read-only) for VISIT and PHONE_CALL */}
                    {note.noteType !== 'NOTE' && (
                        <>
                            <DialogForm.Item className="items-start">
                                <DialogForm.Label icon={CalendarIcon}>
                                    {t('notes__form__date_label', { ns: 'notes' })}
                                </DialogForm.Label>
                                <DialogForm.Body className="py-1.5 px-2">
                                    <Text color="neutral-950">
                                        {intlFormat(
                                            note.occurredAt,
                                            {
                                                month: 'long',
                                                day: 'numeric',
                                                year: 'numeric',
                                            },
                                            { locale },
                                        )}
                                    </Text>
                                </DialogForm.Body>
                            </DialogForm.Item>
                            <DialogForm.Item className="items-start">
                                <DialogForm.Label icon={ClockIcon}>
                                    {t('notes__form__time_label', { ns: 'notes' })}
                                </DialogForm.Label>
                                <DialogForm.Body className="py-1.5 px-2">
                                    <Text color="neutral-950">
                                        {intlFormat(
                                            note.occurredAt,
                                            {
                                                hour: 'numeric',
                                                minute: 'numeric',
                                                hour12: false,
                                            },
                                            { locale },
                                        )}
                                    </Text>
                                </DialogForm.Body>
                            </DialogForm.Item>
                        </>
                    )}
                </DialogForm.ItemList>
                <NoteFormSection
                    noteType={note.noteType}
                    noteId={note.noteId}
                    control={control}
                    errors={formErrors}
                    onVoiceTranscription={(text) => {
                        const next = appendPlainTextToLexical(getValues('title'), text);
                        setValue('title', next, { shouldDirty: true });
                    }}
                />
            </DialogForm.Content>
            <DialogForm.Footer className="justify-between">
                <Button variant="link-danger" icon={Trash2Icon} onClick={handleDeleteClick}>
                    {t('form__delete', { ns: 'common' })}
                </Button>
                <div className="flex gap-3">
                    {onCancel && (
                        <Button variant="subtle" onClick={onCancel} className="min-w-24">
                            {t('form__cancel', { ns: 'common' })}
                        </Button>
                    )}
                    <Button
                        variant="primary"
                        className="min-w-24"
                        onClick={() => {
                            void onSubmit();
                        }}
                        disabled={updateMutation.isPending || !!currentSessionId}
                    >
                        {t('notes__form_edit_save', { ns: 'notes' })}
                    </Button>
                </div>
            </DialogForm.Footer>
        </DialogForm>
    );
}

type EditNoteDialogProps = ModalInnerProps & {
    noteId: string;
    onSuccess?: () => void;
    onDelete?: () => void;
};

export const EditNoteDialog = DialogStateManager.create<EditNoteDialogProps>(
    ({ noteId, onSuccess, onDelete }) => {
        const { visible, remove } = useDialog();
        const [isDirty, setIsDirty] = useState(false);
        const { confirmDiscard } = useDiscardChangesWarning(isDirty);

        const { data: note, isLoading } = api.platoUser.query.notes.getNoteById.useQuery(
            { noteId },
            { enabled: !!noteId },
        );

        const handleClose = (): void => {
            confirmDiscard(remove);
        };

        const handleDelete = (): void => {
            onDelete?.();
            remove();
        };

        const handleSuccess = (): void => {
            remove();
            onSuccess?.();
        };

        return (
            <Dialog open={visible} onClose={handleClose} disablePointerDismissal>
                {isLoading || !note ? (
                    <DialogForm className="sm:max-w-[450px]">
                        <DialogForm.Content>
                            <LoadingBlockFullPage className="mt-16" />
                        </DialogForm.Content>
                    </DialogForm>
                ) : (
                    <EditNoteForm
                        note={note}
                        onCancel={handleClose}
                        onSuccess={handleSuccess}
                        onDeleted={handleDelete}
                        onIsDirtyChange={setIsDirty}
                    />
                )}
            </Dialog>
        );
    },
);
